import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import baseUrl from 'Base_Url/baseUrl';
import {
  Box,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  useDisclosure,
  Icon,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useColorModeValue,
  useToast,
  Tooltip,
  IconButton,
  Flex,
  Skeleton,
  CloseButton,
} from '@chakra-ui/react';
import { SearchIcon, AddIcon } from '@chakra-ui/icons';
import { FaBuilding } from 'react-icons/fa';
import { PiBuildingApartmentFill } from 'react-icons/pi';
import { RiDeleteBin2Line } from 'react-icons/ri';
import Swal from 'sweetalert2';
import { MdEditSquare } from "react-icons/md";

const DivisionSection = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [newDivision, setNewDivision] = useState('');
  const [editingDivision, setEditingDivision] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [divisions, setDivisions] = useState([]);
  const toast = useToast();
  const [divisionLoading, setDivisionLoading] = useState(false);
  const token = localStorage.getItem('token');
  const bgBox = useColorModeValue('white', 'gray.900');
  const bgModalBody = useColorModeValue('white', 'gray.800');
  const textColorHeading = useColorModeValue('blue.800', 'blue.200');
  const modalFooterBg = useColorModeValue('gray.50', 'gray.700');
  const modalFooterBorder = useColorModeValue('gray.200', 'gray.600');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.300');
  const boxShadow = useColorModeValue('md', 'dark-lg');
  const rowHoverBg = useColorModeValue('#f1f5f9', 'gray.600');
  const inputTextColor = useColorModeValue('gray.800', 'white');

  const deleteTimeouts = useRef({});
  const [undoDivision, setUndoDivision] = useState(null);


  const fetchDivisions = useCallback(
    async (search = '') => {
      setDivisionLoading(true);
      try {
        const response = await axios.get(`${baseUrl}/master/all-divisions`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { search },
        });
        const divisionsArray = response.data?.data?.data || [];

        divisionsArray.sort((a, b) => a.name.localeCompare(b.name));
        setDivisions(divisionsArray);
      } catch (error) {
        toast({
          title: 'Error fetching divisions',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setDivisionLoading(false);
      }
    },
    [token, toast],
  );

  useEffect(() => {
    fetchDivisions();
  }, [fetchDivisions]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchDivisions(searchInput);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput, fetchDivisions]);


  const handleSaveDivision = useCallback(async () => {
    if (newDivision.trim() === '') return;
    setLoading(true);
    try {
      if (editingDivision) {
        await axios.put(
          `${baseUrl}/master/edit-division/${editingDivision._id}`,
          { name: newDivision.trim() },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast({
          title: 'Division updated successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        await axios.post(
          `${baseUrl}/master/add-division`,
          { name: newDivision.trim() },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast({
          title: 'Division added successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }

      setNewDivision('');
      setEditingDivision(null);
      onClose();
      fetchDivisions();
    } catch (error) {
      toast({
        title: editingDivision ? 'Failed to update division' : 'Failed to add division',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [newDivision, token, onClose, toast, fetchDivisions, editingDivision]);

  const handleCancel = useCallback(() => {
    setNewDivision('');
    setEditingDivision(null);
    onClose();
  }, [onClose]);

  const handleEditDivision = (division) => {
    setEditingDivision(division);
    setNewDivision(division.name);
    onOpen();
  };

  const handleDeleteDivision = useCallback(
    (id) => {
      const divisionToDelete = divisions.find((d) => d._id === id);

      Swal.fire({
        title: 'Are you sure?',
        text: 'You will not be able to recover this division!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#1f580cff',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!',
      }).then((result) => {
        if (result.isConfirmed) {
          setDivisions((prev) => prev.filter((d) => d._id !== id));
          setUndoDivision(divisionToDelete);
          const toastId = `delete-toast-${id}`;

          toast({
            id: toastId,
            position: 'bottom-left',
            duration: 5000,
            render: ({ onClose }) => (
              <Flex
                align="center"
                justify="space-between"
                p={3}
                bg="gray.700"
                color="white"
                borderRadius="md"
                boxShadow="lg"
              >
                <Text fontSize="sm" mr={4}>
                  "{divisionToDelete.name}" deleted
                </Text>
                <Button
                  variant="ghost"
                  colorScheme="blue"
                  size="sm"
                  onClick={() => {
                    clearTimeout(deleteTimeouts.current[id]);
                    delete deleteTimeouts.current[id];
                    setDivisions((prev) =>
                      [...prev, divisionToDelete].sort((a, b) =>
                        a.name.localeCompare(b.name),
                      ),
                    );
                    setUndoDivision(null);
                    onClose();
                  }}
                >
                  Undo
                </Button>
                <CloseButton onClick={onClose} color="whiteAlpha.800" />
              </Flex>
            ),
          });

          deleteTimeouts.current[id] = setTimeout(async () => {
            try {
              await axios.delete(`${baseUrl}/master/delete-division/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
              });
              setUndoDivision(null);
            } catch (error) {
              toast({
                title: 'Error deleting division',
                status: 'error',
                duration: 3000,
                isClosable: true,
              });
            }
          }, 5000);
        }
      });
    },
    [token, toast, divisions],
  );

  useEffect(() => {
    return () => {
      Object.values(deleteTimeouts.current).forEach(clearTimeout);
    };
  }, []);

  return (
    <Box
      p={4}
      bg={bgBox}
      shadow={boxShadow}
      borderRadius="md"
      border="1px solid"
      borderColor={borderColor}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Flex align="center" gap={2}>
          <Icon as={PiBuildingApartmentFill} boxSize={5} color="#4B80DE" />
          <Heading size="md">Divisions</Heading>
        </Flex>

        <Tooltip label="Add Division" hasArrow>
          <IconButton
            colorScheme="blue"
            size="sm"
            onClick={() => {
              setEditingDivision(null);
              setNewDivision('');
              onOpen();
            }}
            icon={<AddIcon />}
            aria-label="Add Division"
            rounded="full"
            _hover={{ bg: 'blue.600' }}
          />
        </Tooltip>
      </Box>

      <Flex mb={4} gap={4} wrap="wrap">
        <InputGroup width="35%" size="sm">
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.400" />
          </InputLeftElement>
          <Input
            placeholder="Search Division"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            color={inputTextColor}
          />
        </InputGroup>
      </Flex>

      <Box
        bg={useColorModeValue('white', 'gray.800')}
        rounded="lg"
        overflow="hidden"
        mt={4}
        maxH="260px"
        overflowY="auto"
        css={{
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-thumb': { background: '#CBD5E0', borderRadius: '6px' },
        }}
      >
        <Table variant="simple" size="sm">
          <Thead
            position="sticky"
            top={0}
            zIndex={1}
            bgGradient="linear(to-r, blue.400, green.400)"
          >
            <Tr>
              <Th textAlign="center" color="white" fontSize="xs">
                Sr.No.
              </Th>
              <Th color="white" fontSize="xs">
                Divisions
              </Th>
              <Th></Th>
            </Tr>
          </Thead>
          <Tbody>
            {divisionLoading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <Tr key={index}>
                  <Td textAlign="center"><Skeleton height="10px" /></Td>
                  <Td><Skeleton height="10px" /></Td>
                  <Td><Skeleton height="10px" /></Td>
                </Tr>
              ))
            ) : divisions.length === 0 ? (
              <Tr>
                <Td colSpan={3} textAlign="center" py={6}>
                  <Text color="gray.400">No Divisions Found</Text>
                </Td>
              </Tr>
            ) : (
              divisions.map((division, index) => (
                <Tr key={division._id} _hover={{ bg: rowHoverBg }} transition="background 0.2s">
                  <Td textAlign="center" fontSize={'12px'}>{index + 1}</Td>
                  <Td fontSize={'12px'}>{division.name.toUpperCase()}</Td>
                  <Td>
                    <Flex gap={2}>
                      {/* <IconButton
                        icon={<MdEditSquare />}
                        aria-label="Edit"
                        variant="solid"
                        size="sm"
                        colorScheme="blue"
                        isRound
                        onClick={() => handleEditDivision(division)}
                      /> */}

                      <IconButton
                        icon={<MdEditSquare />}
                        aria-label="Edit"
                        variant="ghost"        
                        size="sm"
                        color="blue.500"         
                        _hover={{ bg: 'blue.50', color: 'blue.600' }} 
                        onClick={() => handleEditDivision(division)}
                      />

                      {/* <IconButton
                        icon={<RiDeleteBin2Line />}
                        aria-label="Delete"
                        variant="ghost"
                        size="sm"
                        color="red.500"
                        _hover={{ color: 'blue.600' }}
                        onClick={() => handleDeleteDivision(division._id)}
                      /> */}
                    </Flex>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>


      <Modal isOpen={isOpen} onClose={handleCancel} isCentered>
        <ModalOverlay />
        <ModalContent rounded="lg" shadow="lg" overflow="hidden">
          <Box bg="blue.600" py={1} px={2} textAlign="center" roundedTop="lg">
            <ModalHeader
              fontSize="2xl"
              fontWeight="bold"
              color="white"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Icon as={AddIcon} mr={2} />
              {editingDivision ? 'Edit Division' : 'Add New Division'}
            </ModalHeader>
            <ModalCloseButton color="white" />
          </Box>
          <ModalBody bg={bgModalBody} px={6} py={4}>
            <Text fontSize="lg" fontWeight="bold" color={textColorHeading} mb={2}>
              <Icon as={FaBuilding} mr={2} />
              Division Details
            </Text>
            <FormControl isRequired>
              <FormLabel>Division Name</FormLabel>
              <Input
                placeholder="Enter Division Name"
                value={newDivision}
                onChange={(e) => setNewDivision(e.target.value)}
                color={inputTextColor}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter bg={modalFooterBg} borderTop="1px solid" borderColor={modalFooterBorder}>
            <Button variant="ghost" colorScheme="red" mr={3} onClick={handleCancel}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleSaveDivision} isLoading={loading}>
              <Icon as={AddIcon} mr={2} />
              {editingDivision ? 'Update Division' : 'Add Division'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default DivisionSection;
